import React, { useMemo, useRef, useEffect, useState } from "react";
import { Card, Elevation } from "@blueprintjs/core";
import { useLanguage } from "../context/LanguageContext";

// --- Hilfsfunktionen für Alias-Auflösung und Gruppierung ---

function resolveGlossarEntry(
    glossar: Record<string, string | { alias: string }>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

function getCanonicalGroups(glossar: Record<string, string | { alias: string }>) {
    const canonicalMap: Record<string, string[]> = {};
    Object.keys(glossar).forEach(key => {
        const resolved = resolveGlossarEntry(glossar, key);
        if (!resolved) return;
        if (!canonicalMap[resolved.key]) canonicalMap[resolved.key] = [];
        canonicalMap[resolved.key].push(key);
    });
    return canonicalMap;
}

function groupGlossarByFirstLetterCanonical(
    glossar: Record<string, string | { alias: string }>
): Record<string, { canonical: string; synonyms: string[]; text: string }[]> {
    const canonicalGroups = getCanonicalGroups(glossar);
    const grouped: Record<string, { canonical: string; synonyms: string[]; text: string }[]> = {};
    Object.entries(canonicalGroups).forEach(([canonical, synonyms]) => {
        const letter = canonical[0].toUpperCase();
        if (!grouped[letter]) grouped[letter] = [];
        grouped[letter].push({
            canonical,
            synonyms: synonyms.sort((a, b) => a.localeCompare(b, "de")),
            text: resolveGlossarEntry(glossar, canonical)!.text
        });
    });
    Object.keys(grouped).forEach(letter => {
        grouped[letter].sort((a, b) => a.canonical.localeCompare(b.canonical, "de"));
    });
    return Object.fromEntries(
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b, "de"))
    );
}

// Tooltip für Glossarbegriffe (nutzt resolveGlossarEntry für Auflösung)
const BegriffTooltip: React.FC<{
    begriff: string;
    children: React.ReactNode;
    inGlossarDrawer?: boolean; // Neuer Parameter, um zu erkennen, ob wir uns im Glossar-Drawer befinden
    onTermClick?: (term: string) => void; // Neuer Parameter für den Klick auf einen Begriff
}> = ({ begriff, children, inGlossarDrawer = false, onTermClick }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const { t } = useLanguage();
    const glossar = t('glossary', 'terms', { returnObjects: true }) as Record<string, string | { alias: string }>;
    const resolved = resolveGlossarEntry(glossar, begriff);

    // Handler für Klicks - wenn im Drawer, nur zum Begriff scrollen
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (inGlossarDrawer && onTermClick) {
            onTermClick(begriff);
        } else {
            setVisible((v: boolean) => !v);
        }
    };

    // Wenn im Drawer sind, keine Hover-Events aktivieren
    const mouseEvents = inGlossarDrawer ? {} : {
        onMouseEnter: () => setVisible(true),
        onMouseLeave: () => setVisible(false)
    };

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: "#e7f1fa",
                color: "#2563eb",
                borderRadius: 5,
                padding: "0 4px",
                transition: "background 0.2s",
                fontWeight: 500,
                zIndex: 30,
                display: "inline-block"
            }}
            {...mouseEvents}
            onClick={handleClick}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {/* Tooltip nur anzeigen, wenn wir nicht im Glossar-Drawer sind und visible ist true */}
                {visible && !inGlossarDrawer && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 400,
                            maxWidth: 700,
                            background: "#fff",
                            color: "#222",
                            border: "1.5px solid #bcd3f6",
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "28px 32px",
                            fontWeight: 400,
                            fontSize: 17,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-wrap",
                            marginTop: 12
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// --- Hilfsfunktion: Text mit Glossar-Links als Buttons ausstatten ---
function linkGlossarTextWithTooltip(
    text: string,
    allTerms: string[],
    onClick: (term: string) => void,
    currentCardTerms: string[],
    disableButton?: boolean, // für Punkt 3
    inGlossarDrawer?: boolean // Neuer Parameter, um zu erkennen, ob wir uns im Drawer befinden
): React.ReactNode[] {
    if (allTerms.length === 0) return [text];
    const sortedTerms = [...allTerms].sort((a, b) => b.length - a.length);
    const regex = new RegExp(
        `\\b(${sortedTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("|")})\\b`,
        "gi"
    );
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyCount = 0;
    while ((match = regex.exec(text))) {
        const { index } = match;
        if (lastIndex < index) {
            nodes.push(text.slice(lastIndex, index));
        }
        const term = match[0];
        if (currentCardTerms.some(cardTerm => cardTerm.toLowerCase() === term.toLowerCase())) {
            // Punkt 3: Nur blau hinterlegt, kein Button, kein Unterstrich
            nodes.push(
                <BegriffTooltip
                    begriff={term}
                    key={`glink-${keyCount++}`}
                    inGlossarDrawer={inGlossarDrawer}
                    onTermClick={onClick}
                >
                    <span>{term}</span>
                </BegriffTooltip>
            );
        } else if (!disableButton) {
            nodes.push(
                <BegriffTooltip
                    begriff={term}
                    key={`glink-${keyCount++}`}
                    inGlossarDrawer={inGlossarDrawer}
                    onTermClick={onClick}
                >
                    <button
                        style={{
                            background: "none",
                            border: "none",
                            color: "#000",
                            cursor: "pointer",
                            padding: 0,
                            margin: 0,
                            font: "inherit"
                        }}
                        onClick={e => {
                            e.preventDefault();
                            onClick(term);
                        }}
                        tabIndex={0}
                    >
                        {term}
                    </button>
                </BegriffTooltip>
            );
        } else {
            nodes.push(term);
        }
        lastIndex = index + term.length;
    }
    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }
    return nodes;
}

// ----------- Glossar-Komponente ------------

export const Glossar: React.FC<{ showCards?: boolean }> = ({ showCards = true }) => {
    const { t } = useLanguage();
    const glossar = t('glossary', 'terms', { returnObjects: true }) as Record<string, string | { alias: string }>;
    const grouped = useMemo(() => groupGlossarByFirstLetterCanonical(glossar), [glossar]);
    const letterRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const containerRef = useRef<HTMLDivElement>(null);

    // Alle Glossarbegriffe (inklusive Aliase!)
    const allTerms = useMemo(() => Object.keys(glossar), [glossar]);
    const letters = Object.keys(grouped);

    // Case-insensitive Mapping: Begriff (lowercase) → kanonischer Begriff
    const termToCanonical = useMemo(() => {
        const map: Record<string, string> = {};
        Object.keys(glossar).forEach(key => {
            const resolved = resolveGlossarEntry(glossar, key);
            if (resolved) {
                map[key.toLowerCase()] = resolved.key;
            }
        });
        return map;
    }, [glossar]);

    // Aktiver Buchstabe beim Scrollen
    const [activeLetter, setActiveLetter] = useState(letters[0]);

    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;
            const bounds = containerRef.current.getBoundingClientRect();
            let found = letters[0];
            for (const letter of letters) {
                const el = letterRefs.current[letter];
                if (el) {
                    const elBounds = el.getBoundingClientRect();
                    if (elBounds.top - bounds.top <= 30) {
                        found = letter;
                    }
                }
            }
            setActiveLetter(found);
        };
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll, { passive: true });
        }
        return () => {
            if (container) container.removeEventListener("scroll", handleScroll);
        };
    }, [letters]);

    // Zu Buchstaben scrollen
    function scrollToLetter(letter: string) {
        const ref = letterRefs.current[letter];
        const container = containerRef.current;
        if (ref && container) {
            container.scrollTo({
                top: ref.offsetTop - 18,
                behavior: "smooth"
            });
        }
    }

    // Zu Glossar-Eintrag scrollen (verbesserte Zuverlässigkeit)
    function scrollToTerm(term: string) {
        const canonical = termToCanonical[term.toLowerCase()];
        if (!canonical) return;
        // Scroll per requestAnimationFrame für bessere Zuverlässigkeit
        setTimeout(() => {
            const ref = cardRefs.current[canonical];
            const container = containerRef.current;
            if (ref && container) {
                container.scrollTo({
                    top: ref.offsetTop - 18,
                    behavior: "smooth"
                });
                ref.focus?.();
            } else {
                scrollToLetter(canonical[0].toUpperCase());
            }
        }, 10);
    }

    return (
        <div style={{ display: "flex", maxWidth: 950, margin: "0 auto", position: "relative", minHeight: 500 }}>
            {/* Alphabet-Band */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    userSelect: "none",
                    height: "fit-content",
                    paddingRight: 12,
                }}
                aria-label="Alphabetische Navigation"
            >
                {letters.map(letter => (
                    <button
                        key={letter}
                        type="button"
                        onClick={() => scrollToLetter(letter)}
                        style={{
                            background: activeLetter === letter ? "#2563eb" : "#e7f1fa",
                            color: activeLetter === letter ? "#fff" : "#2563eb",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            padding: "4px 10px",
                            margin: "2px 0",
                            fontWeight: 700,
                            fontSize: 17,
                            boxShadow: activeLetter === letter ? "0 0 0 2px #bcd3f6" : undefined,
                            transition: "background 0.2s, color 0.2s"
                        }}
                        aria-label={`Zu Begriffen mit ${letter} springen`}
                    >
                        {letter}
                    </button>
                ))}
            </div>
            {/* Glossar-Inhalte */}
            <div
                ref={containerRef}
                style={{
                    flex: 1,
                    maxHeight: "70vh",
                    overflowY: "auto",
                    paddingLeft: 48,
                    paddingRight: 8,
                    borderLeft: "1.5px solid #e3e6f0",
                }}
            >
                <h2 style={{ textAlign: "center", marginBottom: 32 }}>{t('glossary', 'title')}</h2>
                {letters.map(letter => (
                    <div
                        key={letter}
                        ref={el => { letterRefs.current[letter] = el; }}
                    >
                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                margin: "35px 0 8px 0",
                                color: "#2452a5",
                                borderBottom: "1.5px solid #e3e6f0",
                                width: "100px"
                            }}
                            id={`glossar-letter-${letter}`}
                        >
                            {letter}
                        </div>
                        {/* Punkt 2: Karten nur zeigen, wenn showCards=true */}
                        {showCards &&
                            grouped[letter].map(({ canonical, synonyms, text }) => (
                                <Card
                                    key={canonical}
                                    elevation={Elevation.ONE}
                                    style={{ marginBottom: 18 }}
                                    ref={el => { cardRefs.current[canonical] = el; }}
                                    tabIndex={-1}
                                >
                                    <b>
                                        {synonyms.map((s, i) =>
                                            <React.Fragment key={s}>
                                                {i > 0 && ", "}
                                                {s === canonical ? <u>{s}</u> : <span>{s}</span>}
                                            </React.Fragment>
                                        )}
                                    </b>
                                    <div style={{ marginTop: 8 }}>
                                        {linkGlossarTextWithTooltip(
                                            text,
                                            allTerms,
                                            scrollToTerm,
                                            synonyms,
                                            false,
                                            true // Wichtig: Hier geben wir an, dass wir im Glossar-Drawer sind
                                        )}
                                    </div>
                                </Card>
                            ))}
                    </div>
                ))}
            </div>
        </div>
    );
};